import { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Chip, CircularProgress, TextField } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Acc } from '../../../../components/edit-object/design/Accordion';
import { useCatalogKeywords } from '../../../../hooks/useCatalogKeywords';
import { useKeywordSuggestions } from '../../../../hooks/useKeywordSuggestions';
import { useAutosaveStatus } from '../../../../hooks/useAutosaveStatus';
import { useWizardCompletion } from '../../../../hooks/useWizardCompletion';
import { logger } from '../../../../utils/logger';

interface Props {
  catalogItemId?: string;
  ownerId?: string | null;
}

/**
 * Keywords accordion that lives inside the Artwork Details step.
 *
 * Owns its own load/save lifecycle through {@link useCatalogKeywords} —
 * the keywords row is a separate DynamoDB table from artwork details and
 * also shares the storage record with Inventory & Albums (custom catalog
 * numbers). To avoid clobbering the inventory fields on save, we always
 * round-trip the loaded `aliCatalogNumber`, `customCatalogNumbers`, and
 * `stepNotes` untouched and only mutate `keywords`.
 */
export const KeywordsSection = ({ catalogItemId, ownerId }: Props) => {
  const { loadKeywords, updateKeywords } = useCatalogKeywords(catalogItemId, ownerId);
  // Defer the /api/keywords/suggestions fetch until the user interacts with
  // the typeahead. The endpoint full-scans the user's keywords (O(catalog
  // items)) and is only useful when the Add-keyword field is in use; the
  // section's accordion is collapsed by default, so eager fetching on mount
  // pays the cost for UI the user can't see and (under fan-out) starves the
  // backend's single Uvicorn worker.
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  const { suggestions, isLoading: suggestionsLoading } = useKeywordSuggestions(
    ownerId ?? undefined,
    { enabled: suggestionsEnabled },
  );
  const { setStatus, setLastSaveTime } = useAutosaveStatus();
  const { markStepComplete } = useWizardCompletion();

  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  // Initial-load guard kept entirely in a ref so save callbacks can read it
  // without re-render churn (and to avoid an in-effect setState on the
  // load path).
  const isLoadingInitialDataRef = useRef(!!catalogItemId);

  // Preserve sibling fields on save (custom catalog numbers, ali number, step notes)
  // so this section never overwrites Inventory & Albums data.
  const siblingFieldsRef = useRef<{
    aliCatalogNumber: string;
    customCatalogNumbers: string[];
    stepNotes: Record<string, string[]>;
  }>({ aliCatalogNumber: '', customCatalogNumbers: [], stepNotes: {} });

  const loadedCatalogIdRef = useRef<string | null>(null);
  const lastSaveRef = useRef<string>(JSON.stringify([]));
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!catalogItemId || loadedCatalogIdRef.current === catalogItemId) {
      return;
    }
    loadedCatalogIdRef.current = catalogItemId;
    isLoadingInitialDataRef.current = true;
    loadKeywords()
      .then((data) => {
        if (data) {
          setKeywords(data.keywords || []);
          siblingFieldsRef.current = {
            aliCatalogNumber: data.aliCatalogNumber || '',
            customCatalogNumbers: data.customCatalogNumbers || [],
            stepNotes: data.stepNotes || {},
          };
          lastSaveRef.current = JSON.stringify(data.keywords || []);
          markStepComplete('catalogKeywords', 1, (data.keywords || []).length > 0);
        }
      })
      .catch((err) => {
        // Default to empty keywords. Surface the failure for debugging —
        // the underlying hook also logs, but this site adds context about
        // what the user will see (an empty section, no error toast).
        logger.error('KeywordsSection - failed to load keywords; defaulting to empty list', err);
      })
      .finally(() => {
        isLoadingInitialDataRef.current = false;
      });
  }, [catalogItemId, loadKeywords, markStepComplete]);

  // Keep sidebar checkmark live as the user edits, mirroring CatalogKeywordsTab.
  useEffect(() => {
    markStepComplete('catalogKeywords', 1, keywords.length > 0);
  }, [keywords, markStepComplete]);

  const saveKeywords = useCallback(async () => {
    if (!catalogItemId || isLoadingInitialDataRef.current) return;
    const currentDataString = JSON.stringify(keywords);
    if (currentDataString === lastSaveRef.current) return;

    setStatus('saving');
    try {
      await updateKeywords({
        ...siblingFieldsRef.current,
        keywords,
      });
      lastSaveRef.current = currentDataString;
      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (err) {
      setStatus('error');
      logger.error('KeywordsSection - Failed to save keywords', err);
    }
  }, [catalogItemId, keywords, updateKeywords, setStatus, setLastSaveTime]);

  const saveKeywordsRef = useRef(saveKeywords);
  useEffect(() => {
    saveKeywordsRef.current = saveKeywords;
  }, [saveKeywords]);

  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveKeywordsRef.current(), 2000);
  }, []);

  // Flush pending save on unmount; never flush during initial load.
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (isLoadingInitialDataRef.current) return;
      try {
        saveKeywordsRef.current()?.catch((err: unknown) =>
          logger.error('KeywordsSection - flush save failed on unmount', err),
        );
      } catch (err) {
        logger.error('KeywordsSection - flush save failed on unmount (sync)', err);
      }
    };
  }, []);

  /**
   * Parses a comma/whitespace-separated input into the deduped (case-insensitive)
   * keyword list and queues a save if anything new was added. Used by both the
   * "Add" button / Enter-key path and the Autocomplete onChange path.
   */
  const commitKeywordInput = (raw: string) => {
    const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = parts.reduce((acc, part) => {
      if (acc.some((k) => k.toLowerCase() === part.toLowerCase())) return acc;
      return [...acc, part];
    }, keywords);
    if (next.length !== keywords.length) {
      setKeywords(next);
      debouncedSave();
    }
    setKeywordInput('');
  };

  const addKeyword = () => commitKeywordInput(keywordInput);

  const removeKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
    debouncedSave();
  };

  const availableSuggestions = suggestions.filter(
    (s) => !keywords.some((k) => k.toLowerCase() === s.toLowerCase()),
  );

  const summary =
    keywords.length === 0
      ? ''
      : keywords.length <= 8
        ? keywords.join(', ')
        : `${keywords.slice(0, 8).join(', ')} +${keywords.length - 8} more`;

  return (
    <Acc
      id="sec-keywords"
      title="Keywords"
      summary={summary}
      emptyPlaceholder="No keywords added"
      defaultExpanded={false}
    >
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Autocomplete
          freeSolo
          options={availableSuggestions}
          inputValue={keywordInput}
          onInputChange={(_event, newValue, reason) => {
            if (reason !== 'reset') setKeywordInput(newValue);
          }}
          onChange={(_event, value) => {
            if (typeof value === 'string' && value.trim()) {
              commitKeywordInput(value);
            }
          }}
          onFocus={() => {
            // Triggers the lazy fetch the first time the user interacts.
            // No-ops on subsequent focus once state is true.
            if (!suggestionsEnabled) setSuggestionsEnabled(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addKeyword();
            }
          }}
          filterOptions={(options, state) =>
            state.inputValue
              ? options.filter((opt) => opt.toLowerCase().includes(state.inputValue.toLowerCase()))
              : options
          }
          loading={suggestionsLoading}
          fullWidth
          clearOnBlur={false}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add a keyword"
              helperText="Press Enter or use commas to add multiple"
              variant="outlined"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {suggestionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        <Button variant="outlined" onClick={addKeyword} disabled={!keywordInput.trim()}>
          Add
        </Button>
      </Box>

      {keywords.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {keywords.map((keyword, index) => (
            <Chip
              key={`${keyword}-${index}`}
              label={keyword}
              onDelete={() => removeKeyword(index)}
              deleteIcon={<Close />}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Acc>
  );
};
